import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { mergeProps } from "@base-ui/react/merge-props";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { Fragment } from "react";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:border-transparent focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "waffle text-primary-foreground hover:bg-primary/90 disabled:opacity-100 pressable",
        destructive:
          "button-destructive hover:bg-destructive/90 disabled:opacity-100 pressable",
        outline:
          "interaction-overlay border border-input hover:text-accent-foreground interactive pressable",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "interaction-overlay hover:text-accent-foreground",
        link: "!p-0 !h-auto text-muted-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-6 rounded-md px-2 gap-1 text-xs [&_svg]:size-3.5",
        lg: "h-11 rounded-lg px-6 text-md",
        icon: "h-9 w-9",
        "icon-sm": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    Omit<ButtonPrimitive.Props, "className">,
    VariantProps<typeof buttonVariants> {
  className?: string;
  busy?: boolean;
}

interface ButtonRenderElementProps extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      children,
      busy,
      render,
      nativeButton,
      ...props
    },
    ref,
  ) => {
    const resolvedClassName = cn(buttonVariants({ variant, size, className }));
    const resolvedChildren = busy ? (
      <Fragment>
        <Loader2 className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 animate-spin" />
        <div className="opacity-0">{children}</div>
      </Fragment>
    ) : (
      children
    );

    // Base UI's Button intentionally gives non-button render targets
    // `role="button"`. Button-styled links should retain native link semantics.
    if (
      React.isValidElement<ButtonRenderElementProps>(render) &&
      render.type !== "button"
    ) {
      const disabled = busy || props.disabled;
      const renderedProps = mergeProps(
        {
          ...props,
          "aria-disabled": disabled || undefined,
          className: resolvedClassName,
          onClick: disabled
            ? (event: React.MouseEvent) => event.preventDefault()
            : props.onClick,
        },
        render.props,
      );

      return React.cloneElement(render, {
        ...renderedProps,
        children: resolvedChildren,
        ref,
      });
    }

    return (
      <ButtonPrimitive
        className={resolvedClassName}
        ref={ref}
        disabled={busy || props.disabled}
        render={render}
        nativeButton={nativeButton}
        {...props}
      >
        {resolvedChildren}
      </ButtonPrimitive>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
